import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FileSpreadsheet, Loader2 } from 'lucide-react';
import { fetchMonthlyReport } from '../../store/slices/monthlyReportSlice';
import { BREADCRUMB_EXPORT_BTN } from '../../components/Breadcrumb/Breadcrumb';
import EmployeeSearch from '../../components/common/EmployeeSearch';
import employeeService from '../../services/EmployeeService';
import { YEARS, currentYear } from '../../utils/yearRange';
import * as XLSX from 'xlsx';

const STICKY_BOTTOM = -8;
const STICKY_TOP = -24;

const NO_TOTAL_KEYS = ['workDays', 'actualWorkDays'];

const FIELD =
  'h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none focus:border-primary-500 text-gray-700';
const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';

const MONTHS = [
  { value: '01', label: 'January' }, { value: '02', label: 'February' },
  { value: '03', label: 'March' }, { value: '04', label: 'April' },
  { value: '05', label: 'May' }, { value: '06', label: 'June' },
  { value: '07', label: 'July' }, { value: '08', label: 'August' },
  { value: '09', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const now = new Date();
const DEFAULT_YEAR = currentYear;
const DEFAULT_MONTH = String(now.getMonth() + 1).padStart(2, '0');

const r2 = (n) => Math.round(n * 100) / 100;
const fmt = (v) => {
  const n = Number(v) || 0;
  return n === 0 ? '0.00' : n.toFixed(2);
};

const METRIC_KEYS = [
  { key: 'workDays', label: 'No. of Working days' },
  { key: 'casual', label: 'Casual' },
  { key: 'annual', label: 'Annual' },
  { key: 'sick', label: 'Sick' },
  { key: 'absent', label: 'Total Absent' },
  { key: 'late', label: 'Late Attendance' },
  { key: 'short', label: 'Short Leave' },
  { key: 'adj', label: 'Adjusted OT Paid' },
  { key: 'actualWorkDays', label: 'Actual Work Days' },
];

const FIXED_COLS = [
  { key: 'index', label: '#', width: 40 },
  { key: 'serviceNo', label: 'Service No.', width: 90 },
  { key: 'name', label: 'Name', width: 340 },
  { key: 'category', label: 'Category', width: 110 },
  { key: 'status', label: 'Status', width: 80 },
  { key: 'designation', label: 'Designation', width: 320 },
];

const WIDE_COL_WIDTH = 140;

const TABLE_MIN_WIDTH =
  FIXED_COLS.reduce((s, c) => s + c.width, 0) + METRIC_KEYS.length * 2 * WIDE_COL_WIDTH;

const STICKY_LEFT = {
  index: 0,
  serviceNo: 40,
  name: 130,
};

export default function MonthlyReport() {
  const dispatch = useDispatch();
  const { rows, loading, error } = useSelector((s) => s.monthlyReport);

  const [year, setYear] = useState(DEFAULT_YEAR);
  const [month, setMonth] = useState(DEFAULT_MONTH);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [gradeOptions, setGradeOptions] = useState([]);
  const [gradesLoading, setGradesLoading] = useState(false);

  const reportRef = useRef(null);     
  const tableScrollRef = useRef(null);   
  const tableRef = useRef(null);        
  const headerRef = useRef(null);  
  const stickyScrollRef = useRef(null); 
  const syncing = useRef(false);
  const [bar, setBar] = useState({ scrollWidth: 0, hasOverflow: false });

  /* ── Fetch employee grades and build a lookup map ────────────────────── */
  useEffect(() => {
    let cancelled = false;
    const loadGrades = async () => {
      setGradesLoading(true);
      try {
        const response = await employeeService.GetEmployeeList();
        const data = response?.data ?? response;
        const employees = data?.ResultSet ?? data?.resultSet ?? data ?? [];

        const gradeSet = new Set();
        employees.forEach((emp) => {
          const grade = emp.HGD_GRADE_NAME || '';
          if (grade && grade.trim()) gradeSet.add(grade.trim());
        });

        if (!cancelled) {
          setGradeOptions(Array.from(gradeSet).sort((a, b) => a.localeCompare(b)));
        }
      } catch (err) {
        console.error('Failed to load employee grades:', err);
        if (!cancelled) setGradeOptions([]);
      } finally {
        if (!cancelled) setGradesLoading(false);
      }
    };
    loadGrades();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    dispatch(fetchMonthlyReport({ year, month: String(Number(month)) }));
  }, [dispatch, year, month]);

  /* ── Map of employeeId -> grade, used for filtering ──────────────────── */
  const [employeeGradeMap, setEmployeeGradeMap] = useState({});

  useEffect(() => {
    let cancelled = false;
    const buildMap = async () => {
      try {
        const response = await employeeService.GetEmployeeList();
        const data = response?.data ?? response;
        const employees = data?.ResultSet ?? data?.resultSet ?? data ?? [];
        const map = {};
        employees.forEach((emp) => {
          const id = String(emp.HED_EMPLOYEE_ID ?? '');
          const grade = emp.HGD_GRADE_NAME || '';
          if (id) map[id] = grade.trim();
        });
        if (!cancelled) setEmployeeGradeMap(map);
      } catch (err) {
        console.error('Failed to build grade map:', err);
      }
    };
    buildMap();
    return () => { cancelled = true; };
  }, []);

  /* ── Merge grade into rows and filter ────────────────────────────────── */
  const rowsWithGrade = useMemo(() => {
    return rows.map((r) => ({
      ...r,
      grade: employeeGradeMap[String(r.id)] || '',
    }));
  }, [rows, employeeGradeMap]);

  const employeeOptions = useMemo(() => {
    const map = new Map();
    rowsWithGrade.forEach((r) => {
      const id = String(r.employeeId ?? r.serviceNo ?? '');
      if (!id || map.has(id)) return;
      map.set(id, {
        employeeId: id,
        serviceNo: r.serviceNo || '',
        name: r.name || '',
        department: r.category || '',
      });
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.serviceNo || '').localeCompare(b.serviceNo || '')
    );
  }, [rowsWithGrade]);

  const filtered = useMemo(() => {
    let result = rowsWithGrade;

    if (selectedGrade) {
      result = result.filter((r) => r.grade === selectedGrade);
    }

    const empId =
      selectedEmployee && !selectedEmployee.isAll
        ? String(selectedEmployee.employeeId)
        : null;
    if (empId) {
      result = result.filter(
        (r) => String(r.employeeId ?? r.serviceNo ?? '') === empId
      );
    }

    return result;
  }, [rowsWithGrade, selectedEmployee, selectedGrade]);

  const totals = useMemo(() => {
    const sum = (group, key) =>
      filtered.reduce((s, r) => s + (Number(r[group]?.[key]) || 0), 0);
    const build = (group) =>
      METRIC_KEYS.reduce((acc, m) => {
        acc[m.key] = r2(sum(group, m.key));
        return acc;
      }, {});
    return { current: build('current'), total: build('total') };
  }, [filtered]);

  const monthLabel = MONTHS.find((m) => m.value === month)?.label ?? '';
  const groupLabel = `${monthLabel.slice(0, 3)}-${year.slice(2)}`;

  const totalCols = FIXED_COLS.length + METRIC_KEYS.length * 2;

  const measureBar = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const next = {
      scrollWidth: el.scrollWidth,
      hasOverflow: el.scrollWidth > el.clientWidth + 1,
    };
    setBar((prev) =>
      prev.scrollWidth === next.scrollWidth && prev.hasOverflow === next.hasOverflow ? prev : next
    );
  }, []);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    measureBar();
    const ro = new ResizeObserver(measureBar);
    ro.observe(el);
    if (tableRef.current) ro.observe(tableRef.current);
    window.addEventListener('resize', measureBar);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureBar);
    };
  }, [measureBar, loading, error, filtered.length]);

  useEffect(() => {
    if (bar.hasOverflow && stickyScrollRef.current && tableScrollRef.current) {
      stickyScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
  }, [bar.hasOverflow]);

  const handleTableScroll = () => {
    if (headerRef.current && tableScrollRef.current) {
      headerRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
    if (syncing.current) { syncing.current = false; return; }
    if (stickyScrollRef.current && tableScrollRef.current) {
      syncing.current = true;
      stickyScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
  };

  const handleStickyScroll = () => {
    if (syncing.current) { syncing.current = false; return; }
    if (stickyScrollRef.current && tableScrollRef.current) {
      syncing.current = true;
      tableScrollRef.current.scrollLeft = stickyScrollRef.current.scrollLeft;
    }
  };

  /* ── Excel export ───── */
const exportToExcel = () => {
  const table = reportRef.current;      
  if (!table) return;
 
  const wb = XLSX.utils.table_to_book(table, { sheet: 'Monthly Report', raw: true });
  const ws = wb.Sheets['Monthly Report'];
 
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let R = 3; R <= range.e.r; R += 1) {
    for (let C = FIXED_COLS.length; C <= range.e.c; C += 1) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell && cell.t === 's' && cell.v !== '' && !Number.isNaN(Number(cell.v))) {
        cell.t = 'n';
        cell.v = Number(cell.v);
        cell.z = '0.00';
      }
    }
  }

  ws['!cols'] = [
    ...FIXED_COLS.map((c) => ({ wch: Math.max(6, Math.round(c.width / 7)) })),
    ...METRIC_KEYS.flatMap(() => [{ wch: 16 }, { wch: 16 }]).slice(0, METRIC_KEYS.length * 2),
  ];
 
  XLSX.writeFile(wb, `Monthly_Report_${monthLabel}_${year}.xlsx`);
};

  const colGroup = (
    <colgroup>
      {FIXED_COLS.map((c) => (
        <col key={c.key} style={{ width: c.width }} />
      ))}
      {METRIC_KEYS.map((m) => (
        <col key={`cw-${m.key}`} style={{ width: WIDE_COL_WIDTH }} />
      ))}
      {METRIC_KEYS.map((m) => (
        <col key={`tw-${m.key}`} style={{ width: WIDE_COL_WIDTH }} />
      ))}
    </colgroup>
  );

  const tableStyle = { width: '100%', minWidth: `${TABLE_MIN_WIDTH}px`, tableLayout: 'fixed' };

  return (
    <div className="min-h-full bg-surface-50 p-6 space-y-4">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Monthly Report</h1>
          <p className="mt-1 text-sm text-gray-500">
            {monthLabel} {year} — attendance summary for all employees.
          </p>
        </div>
        <button
          onClick={exportToExcel}
          className={BREADCRUMB_EXPORT_BTN}
          disabled={!filtered.length || loading}
        >
          <FileSpreadsheet size={15} />
          Export to Excel
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-gray-200 bg-white px-4 py-3.5 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <span className={LABEL}>Year</span>
            <select value={year} onChange={(e) => setYear(e.target.value)} className={FIELD}>
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <span className={LABEL}>Month</span>
            <select value={month} onChange={(e) => setMonth(e.target.value)} className={FIELD}>
              {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>

          {/* Grade filter */}
          <div>
            <span className={LABEL}>Grade</span>
            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              className={FIELD}
              disabled={gradesLoading}
            >
              <option value="">
                {gradesLoading ? 'Loading…' : 'All Grades'}
              </option>
              {gradeOptions.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[240px] max-w-md">
            <span className={LABEL}>Employee</span>
            <EmployeeSearch
              value={selectedEmployee}
              onSelect={setSelectedEmployee}
              employees={employeeOptions}
              loading={loading}
              showAllOption
            />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">

        {/* Card header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3.5 rounded-t-xl">
          <h3 className="text-sm font-semibold text-gray-900">{monthLabel} {year}</h3>
          <span className="text-xs text-gray-500">
            {filtered.length === 1 ? '1 record' : `${filtered.length} records`}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-sm text-gray-400">
            <Loader2 size={15} className="animate-spin" /> Loading report…
          </div>
        ) : error ? (
          <div className="px-5 py-10 text-center text-sm text-red-500">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-sm text-gray-400">No records found</div>
        ) : (
          <>
            <div
              ref={headerRef}
              className="sticky z-30 overflow-hidden bg-gray-50"
              style={{ top: STICKY_TOP }}
            >
              <table
                className="border-separate border-spacing-0 border-t border-l border-gray-200 text-[12px]"
                style={tableStyle}
              >
                {colGroup}
                <thead>
                  <tr className="bg-gray-50 text-gray-700">
                    {FIXED_COLS.map((c) => {
                      const sticky = c.key in STICKY_LEFT;
                      return (
                        <th
                          key={`g-${c.key}`}
                          rowSpan={2}
                          className="border-b border-r border-gray-200 bg-gray-50 px-2 py-2 text-center text-[12px] font-semibold text-gray-500 align-middle"
                          style={
                            sticky
                              ? { position: 'sticky', left: STICKY_LEFT[c.key], zIndex: 12 }
                              : undefined
                          }
                        >
                          {c.label}
                        </th>
                      );
                    })}
                    <th
                      colSpan={METRIC_KEYS.length}
                      className="border-b border-r border-gray-200 bg-gray-50 px-2 py-2 text-center text-[12px] font-semibold text-gray-700"
                    >
                      {groupLabel}
                    </th>
                    <th
                      colSpan={METRIC_KEYS.length}
                      className="border-b border-r border-gray-200 bg-gray-50 px-2 py-2 text-center text-[12px] font-semibold text-gray-700"
                    >
                      Total
                    </th>
                  </tr>

                  <tr className="bg-gray-50 text-gray-700">
                    {METRIC_KEYS.map((m) => (
                      <th
                        key={`ch-${m.key}`}
                        className="border-b border-r border-gray-200 bg-gray-50 px-2 py-1 text-center text-[11px] font-medium text-gray-500 whitespace-nowrap"
                      >
                        {m.label}
                      </th>
                    ))}
                    {METRIC_KEYS.map((m) => (
                      <th
                        key={`th-${m.key}`}
                        className="border-b border-r border-gray-200 bg-gray-50 px-2 py-1 text-center text-[11px] font-medium text-gray-500 whitespace-nowrap"
                      >
                        {m.label}
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </div>

            <div
              ref={tableScrollRef}
              onScroll={handleTableScroll}
              className="overflow-x-auto [&::-webkit-scrollbar]:hidden"
              style={{ overflowY: 'hidden', scrollbarWidth: 'none' }}
            >
              <table
                ref={tableRef}
                className="border-separate border-spacing-0 border-l border-gray-200 text-[12px]"
                style={tableStyle}
              >
                {colGroup}

                <tbody>
                  {filtered.map((r, i) => (
                    <tr key={r.id ?? r.serviceNo ?? i} className="group">
                      <td
                        className="border-b border-r border-gray-200 bg-white group-hover:bg-gray-50 px-2 py-1 text-center tabular-nums text-gray-400"
                        style={{ position: 'sticky', left: STICKY_LEFT.index, zIndex: 2 }}
                      >
                        {i + 1}
                      </td>
                      <td
                        className="border-b border-r border-gray-200 bg-white group-hover:bg-gray-50 px-2 py-1 text-center tabular-nums text-gray-600"
                        style={{ position: 'sticky', left: STICKY_LEFT.serviceNo, zIndex: 2 }}
                      >
                        {r.serviceNo}
                      </td>
                      <td
                        className="border-b border-r border-gray-200 bg-white group-hover:bg-gray-50 px-2 py-1 font-medium text-gray-900 whitespace-nowrap"
                        style={{ position: 'sticky', left: STICKY_LEFT.name, zIndex: 2 }}
                      >
                        {r.name}
                      </td>

                      <td className="border-b border-r border-gray-200 group-hover:bg-gray-50 px-2 py-1 text-gray-600 whitespace-nowrap truncate">
                        {r.category}
                      </td>
                      <td className="border-b border-r border-gray-200 group-hover:bg-gray-50 px-2 py-1 text-center text-gray-600 truncate">
                        {r.status}
                      </td>
                      <td className="border-b border-r border-gray-200 group-hover:bg-gray-50 px-2 py-1 text-gray-600 whitespace-nowrap truncate">
                        {r.designation}
                      </td>

                      {METRIC_KEYS.map((m) => (
                        <td
                          key={`cv-${m.key}`}
                          className="border-b border-r border-gray-200 group-hover:bg-gray-50 px-2 py-1 text-right tabular-nums text-gray-600"
                        >
                          {fmt(r.current?.[m.key])}
                        </td>
                      ))}
                      {METRIC_KEYS.map((m) => (
                        <td
                          key={`tv-${m.key}`}
                          className="border-b border-r border-gray-200 group-hover:bg-gray-50 px-2 py-1 text-right tabular-nums text-gray-600"
                        >
                          {fmt(r.total?.[m.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>

                <tfoot>
                  <tr className="font-semibold text-gray-900">
                    <td
                      colSpan={2}
                      className="border-b border-r border-gray-200 bg-gray-50 px-2 py-2 text-center text-[12px] uppercase tracking-wide text-gray-500"
                      style={{ position: 'sticky', left: STICKY_LEFT.index, zIndex: 10 }}
                    >
                      TOTAL
                    </td>
                    <td
                      className="border-b border-r border-gray-200 bg-gray-50"
                      style={{ position: 'sticky', left: STICKY_LEFT.name, zIndex: 10 }}
                    />
                    {FIXED_COLS.slice(3).map((c) => (
                      <td
                        key={`cf-${c.key}`}
                        className="border-b border-r border-gray-200 bg-gray-50"
                      />
                    ))}
                    {METRIC_KEYS.map((m) => (
                      <td
                        key={`ct-${m.key}`}
                        className="border-b border-r border-gray-200 bg-gray-50 px-2 py-2 text-right tabular-nums"
                      >
                        {NO_TOTAL_KEYS.includes(m.key) ? '' : fmt(totals.current[m.key])}
                      </td>
                    ))}
                    {METRIC_KEYS.map((m) => (
                      <td
                        key={`tt-${m.key}`}
                        className="border-b border-r border-gray-200 bg-gray-50 px-2 py-2 text-right tabular-nums"
                      >
                        {NO_TOTAL_KEYS.includes(m.key) ? '' : fmt(totals.total[m.key])}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>

            <div
              ref={stickyScrollRef}
              onScroll={handleStickyScroll}
              className="overflow-x-auto overflow-y-hidden rounded-b-xl"
              style={{
                position: 'sticky',
                bottom: STICKY_BOTTOM,
                zIndex: 20,
                height: 16,
                background: '#fff',
                borderTop: '1px solid #e5e7eb',
                display: bar.hasOverflow ? 'block' : 'none',
              }}
              aria-hidden="true"
            >
              <div style={{ width: bar.scrollWidth || TABLE_MIN_WIDTH, height: 1 }} />
            </div>
          </>
        )}
      </div>

      {/* ── Hidden Excel-only table ─────────────────────────────────────── */}
      <div style={{ position: 'absolute', left: -99999, top: 0 }} aria-hidden="true">
        <table
          ref={reportRef}
          border="1"
          cellPadding="4"
          cellSpacing="0"
          style={{ borderCollapse: 'collapse', fontFamily: 'Calibri, Arial, sans-serif', fontSize: '10pt' }}
        >
          <thead>
            <tr>
              <th
                colSpan={totalCols}
                style={{
                  background: '#fef9c3',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '12pt',
                  border: '1px solid #9ca3af',
                }}
              >
                Monthly Staff Work Report — {monthLabel} {year}
              </th>
            </tr>

            <tr>
              {FIXED_COLS.map((c) => (
                <th
                  key={`xg-${c.key}`}
                  rowSpan={2}
                  style={{
                    border: '1px solid #9ca3af',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                  }}
                >
                  {c.label}
                </th>
              ))}
              <th
                colSpan={METRIC_KEYS.length}
                style={{ border: '1px solid #9ca3af', fontWeight: 'bold', textAlign: 'center' }}
              >
                {groupLabel}
              </th>
              <th
                colSpan={METRIC_KEYS.length}
                style={{ border: '1px solid #9ca3af', fontWeight: 'bold', textAlign: 'center' }}
              >
                Total
              </th>
            </tr>

            <tr>
              {METRIC_KEYS.map((m) => (
                <th
                  key={`xch-${m.key}`}
                  style={{ border: '1px solid #9ca3af', fontWeight: 'bold', textAlign: 'center' }}
                >
                  {m.label}
                </th>
              ))}
              {METRIC_KEYS.map((m) => (
                <th
                  key={`xth-${m.key}`}
                  style={{ border: '1px solid #9ca3af', fontWeight: 'bold', textAlign: 'center' }}
                >
                  {m.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filtered.map((r, i) => (
              <tr key={`x-${r.id ?? i}`}>
                <td style={{ border: '1px solid #9ca3af', textAlign: 'center' }}>{i + 1}</td>
                <td style={{ border: '1px solid #9ca3af', textAlign: 'center' }}>{r.serviceNo}</td>
                <td style={{ border: '1px solid #9ca3af' }}>{r.name}</td>
                <td style={{ border: '1px solid #9ca3af' }}>{r.category}</td>
                <td style={{ border: '1px solid #9ca3af', textAlign: 'center' }}>{r.status}</td>
                <td style={{ border: '1px solid #9ca3af' }}>{r.designation}</td>
                {METRIC_KEYS.map((m) => (
                  <td
                    key={`xcv-${m.key}`}
                    style={{ border: '1px solid #9ca3af', textAlign: 'right' }}
                  >
                    {fmt(r.current?.[m.key])}
                  </td>
                ))}
                {METRIC_KEYS.map((m) => (
                  <td
                    key={`xtv-${m.key}`}
                    style={{ border: '1px solid #9ca3af', textAlign: 'right' }}
                  >
                    {fmt(r.total?.[m.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr>
              <td
                colSpan={FIXED_COLS.length}
                style={{
                  background: '#fef9c3',
                  border: '1px solid #9ca3af',
                  fontWeight: 'bold',
                  textAlign: 'center',
                }}
              >
                TOTAL
              </td>
              {METRIC_KEYS.map((m) => (
                <td
                  key={`xct-${m.key}`}
                  style={{
                    background: '#fef9c3',
                    border: '1px solid #9ca3af',
                    fontWeight: 'bold',
                    textAlign: 'right',
                  }}
                >
                  {NO_TOTAL_KEYS.includes(m.key) ? '' : fmt(totals.current[m.key])}
                </td>
              ))}
              {METRIC_KEYS.map((m) => (
                <td
                  key={`xtt-${m.key}`}
                  style={{
                    background: '#fef9c3',
                    border: '1px solid #9ca3af',
                    fontWeight: 'bold',
                    textAlign: 'right',
                  }}
                >
                 {NO_TOTAL_KEYS.includes(m.key) ? '' : fmt(totals.total[m.key])}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}